import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Broadcast, Play, Pause, ArrowsClockwise, Waveform, Warning,
  MapPin, PlusCircle, ShieldWarning,
} from "@phosphor-icons/react";
import { EdgePatrolPanel } from "@/components/EdgePatrolPanel";

const SEV = {
  critical: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
  high: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40",
  medium: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/40",
  low: "bg-[#71717A]/15 text-[#A1A1AA] border-[#71717A]/40",
};

export default function Patrol() {
  const [cams, setCams] = useState([]);
  const [camId, setCamId] = useState("");
  const [patrolling, setPatrolling] = useState(false);
  const [intervalSec, setIntervalSec] = useState(12);
  const [motionOnly, setMotionOnly] = useState(true);
  const [motionThreshold, setMotionThreshold] = useState(12);
  const [theft, setTheft] = useState(70);
  const [current, setCurrent] = useState(null);
  const [log, setLog] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [creating, setCreating] = useState(false);

  const busyRef = useRef(false);
  const patrolRef = useRef(false);
  const scanRef = useRef(null);
  const cfgRef = useRef({});
  cfgRef.current = { motionOnly, motionThreshold, theft, camId };

  const loadCams = () => api.get("/cameras").then((r) => setCams(r.data.filter((c) => c.source === "ptz" || (c.presets && c.presets.length))));
  useEffect(() => { loadCams(); }, []);
  useEffect(() => { if (!camId && cams.length) setCamId(cams[0].id); }, [cams, camId]);

  const ptzCams = cams;

  const createDemo = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/monitor/ptz-demo");
      toast.success("PTZ patrol camera ready");
      await loadCams();
      setCamId(data.camera.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const patrolOnce = useCallback(async () => {
    if (busyRef.current || !cfgRef.current.camId) return;
    busyRef.current = true;
    try {
      const { data } = await api.post("/monitor/patrol", {
        camera_id: cfgRef.current.camId,
        motion_only: cfgRef.current.motionOnly,
        motion_threshold: cfgRef.current.motionThreshold,
        theft_threshold: cfgRef.current.theft,
        vandalism_threshold: cfgRef.current.theft,
        effectiveness_min: 45,
      });
      if (!patrolRef.current) return;
      setCurrent(data);
      setLog((p) => [{ id: `${data.preset}-${Date.now()}`, ...data }, ...p].slice(0, 40));
      if (data.alerts?.length) {
        setAlerts((p) => [...data.alerts, ...p].slice(0, 30));
        data.alerts.forEach((a) => {
          const fn = a.severity === "critical" || a.severity === "high" ? toast.error : toast.warning;
          fn(`${a.type} · ${a.camera}`, { description: a.message });
        });
      }
    } catch (e) {
      if (patrolRef.current) toast.error(e.response?.data?.detail || "Patrol scan failed");
    } finally { busyRef.current = false; }
  }, []);
  scanRef.current = patrolOnce;

  useEffect(() => {
    patrolRef.current = patrolling;
    if (!patrolling) return;
    scanRef.current && scanRef.current();
    const t = setInterval(() => scanRef.current && scanRef.current(), intervalSec * 1000);
    return () => clearInterval(t);
  }, [patrolling, intervalSec]);

  const selected = useMemo(() => cams.find((c) => c.id === camId), [cams, camId]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      {/* Controls */}
      <div className="xl:col-span-4 space-y-4">
        <EdgePatrolPanel />

        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
            <ArrowsClockwise size={16} weight="bold" className="text-[#007AFF]" /> Simulated Patrol
            <span className="font-mono-data text-[9px] px-1.5 py-0.5 bg-[#007AFF]/15 text-[#3395FF] border border-[#007AFF]/40">DEMO</span>
          </h2>
          <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">
            No hardware? This demo camera rotates through preset views to show how patrol works. At each stop it runs <span className="text-[#3395FF]">motion detection</span> and only calls the AI when something moves.
          </p>

          {ptzCams.length === 0 ? (
            <button data-testid="create-ptz-demo" onClick={createDemo} disabled={creating}
              className="w-full flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors disabled:opacity-60">
              <PlusCircle size={16} weight="bold" /> {creating ? "Creating…" : "Create Demo PTZ Camera"}
            </button>
          ) : (
            <>
              <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1.5">Patrol Camera</label>
              <select data-testid="patrol-camera-select" value={camId} onChange={(e) => setCamId(e.target.value)}
                className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#007AFF] transition-colors mb-4">
                {ptzCams.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <button data-testid="patrol-toggle" onClick={() => setPatrolling((p) => !p)}
                className={`w-full py-3 font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 ${patrolling ? "bg-[#FF3B30] hover:bg-[#ff5c52]" : "bg-[#007AFF] hover:bg-[#3395FF]"} text-white`}>
                {patrolling ? <><Pause size={18} weight="fill" /> Stop Patrol</> : <><Play size={18} weight="fill" /> Start Patrol</>}
              </button>

              {selected?.presets && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {selected.presets.map((p, i) => (
                    <span key={p.name} className={`font-mono-data text-[10px] px-1.5 py-0.5 border ${current?.preset === p.name ? "bg-[#007AFF]/20 text-[#3395FF] border-[#007AFF]/50" : "text-[#71717A] border-white/10"}`}>
                      <MapPin size={9} className="inline mr-0.5" />{p.name}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-[#121214] border border-white/10 p-4 space-y-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA]">Settings</h3>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="flex items-center gap-2 text-[#A1A1AA]"><Waveform size={14} weight="bold" /> Motion-gated AI</span>
            <input type="checkbox" data-testid="motion-only" checked={motionOnly} onChange={(e) => setMotionOnly(e.target.checked)} className="accent-[#007AFF] w-4 h-4" />
          </label>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Motion Sensitivity</span><span className="font-mono-data text-sm">{motionThreshold}</span></div>
            <input type="range" min={2} max={50} value={motionThreshold} onChange={(e) => setMotionThreshold(Number(e.target.value))} className="w-full accent-[#007AFF] cursor-pointer" data-testid="motion-threshold" />
            <p className="text-[10px] text-[#71717A] font-mono-data mt-1">Lower = more sensitive. AI runs when motion exceeds this.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Dwell / Interval</span><span className="font-mono-data text-sm">{intervalSec}s</span></div>
            <input type="range" min={6} max={40} value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))} className="w-full accent-[#007AFF] cursor-pointer" data-testid="patrol-interval" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Theft/Vandalism Alert</span><span className="font-mono-data text-sm">{theft}</span></div>
            <input type="range" min={0} max={100} value={theft} onChange={(e) => setTheft(Number(e.target.value))} className="w-full accent-[#007AFF] cursor-pointer" data-testid="patrol-threshold" />
          </div>
        </div>
      </div>

      {/* Live view */}
      <div className="xl:col-span-8 space-y-4">
        <div className="relative aspect-video bg-black border border-white/10 overflow-hidden scanline" data-testid="patrol-view">
          {current?.view ? (
            <img src={current.view} alt={current.preset} className="w-full h-full object-cover opacity-80" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#71717A]"><Broadcast size={40} weight="thin" /></div>
          )}
          <div className="absolute inset-0 bg-black/30" />
          {current && (
            <>
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" />
                <span className="font-mono-data text-xs tracking-widest">PATROL · {current.preset}</span>
                <span className="font-mono-data text-[10px] text-[#71717A]">{current.preset_index + 1}/{current.presets_total}</span>
              </div>
              <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                <span className="font-mono-data text-[10px] uppercase text-[#A1A1AA]">Motion</span>
                <div className="w-24 h-1.5 bg-black/60"><div className="h-full transition-all" style={{ width: `${current.motion}%`, background: current.motion >= motionThreshold ? "#FF9F0A" : "#32D74B" }} /></div>
                <span className="font-mono-data text-[10px]" style={{ color: current.status === "quiet" ? "#71717A" : "#32D74B" }}>{current.status === "quiet" ? "QUIET — AI skipped" : "ANALYZED"}</span>
              </div>
              {current.result && (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                  <p className="text-sm">{current.result.scene_summary}</p>
                  <div className="flex gap-3 font-mono-data text-[10px] mt-1">
                    <span style={{ color: current.result.theft_risk?.score >= theft ? "#FF3B30" : "#71717A" }}>THEFT {current.result.theft_risk?.score ?? 0}</span>
                    <span style={{ color: current.result.vandalism_risk?.score >= theft ? "#FF3B30" : "#71717A" }}>VAND {current.result.vandalism_risk?.score ?? 0}</span>
                    <span className="text-[#71717A]">PEOPLE {current.result.people_count ?? 0}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#121214] border border-white/10">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-black uppercase tracking-tighter text-xs flex items-center gap-2"><ShieldWarning size={14} weight="bold" /> Trouble Flagged</h3>
              <span data-testid="patrol-alert-count" className="font-mono-data text-[10px] px-2 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40">{alerts.length}</span>
            </div>
            <div className="p-3 space-y-2 max-h-[280px] overflow-y-auto">
              {alerts.length === 0 && <p className="text-xs text-[#71717A] font-mono-data p-2">No trouble detected on patrol yet.</p>}
              <AnimatePresence>
                {alerts.map((a) => (
                  <motion.div key={a.id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="border border-white/10 p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-mono-data text-[10px] px-1.5 py-0.5 border uppercase ${SEV[a.severity]}`}>{a.type}</span>
                      <span className="font-mono-data text-[10px] text-[#71717A]">{new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                    </div>
                    <p className="text-xs text-[#A1A1AA]">{a.message}</p>
                    <p className="font-mono-data text-[10px] text-[#71717A] mt-1">{a.camera}</p>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div className="bg-[#121214] border border-white/10">
            <div className="px-4 py-3 border-b border-white/10"><h3 className="font-display font-black uppercase tracking-tighter text-xs">Sweep Log</h3></div>
            <div className="p-3 space-y-1.5 max-h-[280px] overflow-y-auto" data-testid="patrol-log">
              {log.length === 0 && <p className="text-xs text-[#71717A] font-mono-data p-2">Start patrol to sweep the premises…</p>}
              {log.map((e) => (
                <div key={e.id} className="border-b border-white/5 pb-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium flex items-center gap-1"><MapPin size={11} className="text-[#3395FF]" />{e.preset}</span>
                    <span className="font-mono-data text-[10px] text-[#71717A]">{new Date(e.scanned_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  </div>
                  <div className="flex gap-3 font-mono-data text-[10px] mt-0.5">
                    <span style={{ color: e.motion >= motionThreshold ? "#FF9F0A" : "#71717A" }}>MOTION {e.motion}</span>
                    <span className="text-[#71717A]">{e.status === "quiet" ? "quiet" : "analyzed"}</span>
                    {e.alerts?.length > 0 && <span className="text-[#FF3B30]">⚠ {e.alerts.length}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
